/**
 * #5128, df-4ebecd016a94 — what the cold start should do when the database-recovery machinery
 * takes its failure.
 *
 * `postLoginColdStartAsync` wraps the whole cold boot in one try/catch and offers anything that
 * throws to `DatabaseRecoveryService.interceptDatabaseError`. A `true` answer means "I have taken this
 * failure", and it covers three very different outcomes:
 *
 * - the page is being replaced, so the cold start stops here without taking the splash down: the
 *   reload finishes the boot, and taking the splash down first would only flash a half-built app
 *   on the way out.
 * - since #4502, the stored database can instead be repaired IN PLACE with no reload at all. The
 *   cold start used to `return` here too, which skipped `markAppLoadingAsync(false)` — the one
 *   place the loading splash comes down and `auth.whenAppReady` is resolved. So the database
 *   healed and the user watched the splash forever.
 * - nothing was repaired and nothing is going to reload either. Then the cold start is the only
 *   thing that can end the boot, and until df-4ebecd016a94 it did not: it stopped and left the
 *   splash up for the splash watchdog's much slower ladder to notice.
 *
 * The decision lives here, as a plain function with its dependencies passed in, for the same
 * reason `singleton-read-recovery.ts` exists one layer down: `app.component.ts` pulls in Angular
 * and cannot be loaded by the Node test runner, and a branch that only runs when a real
 * IndexedDB fault interrupts a real cold start cannot be staged any other way.
 */

import { DatabaseRecoverySettlement } from '../services/singleton-read-recovery';

/** What the cold start should do once the recovery it triggered has settled. */
export enum ColdStartRecoveryAction {
  /** Stop here without taking the splash down, because something else is going to finish this
   *  boot.
   *
   *  Two situations arrive at this answer. The recovery is replacing the page, in which case the
   *  reload finishes the boot. Or this cold start has been SUPERSEDED by a newer one, which owns
   *  the splash now and will take it down when it is done — see
   *  `decideColdStartRecoveryActionAsync`'s `aNewerColdStartHasBegun` parameter for how that is
   *  known and why it has to be. */
  StopWithoutEndingTheBootScreen = 'StopWithoutEndingTheBootScreen',
  /** Nothing repaired the database, nothing is going to reload the page, and this is still the
   *  newest cold start — so no reload and no other boot is coming, and this boot is the only
   *  thing that can end the loading screen. Report it and show the boot-failure screen.
   *
   *  df-4ebecd016a94. The one arm of `interceptDatabaseError` that answers this way is a call on
   *  a store handle the app closed itself, which runs no recovery on purpose: it says something
   *  about our own lifecycle, not about the device. Stopping quietly here left the splash up
   *  until the splash watchdog escalated — a stuck message after 25 seconds and one guarded
   *  reload roughly 85 to 90 seconds in, or nearer 240 seconds on a boot that keeps emitting
   *  sync progress. The honest screen arrives in a second or two and says what happened, and it
   *  already offers Try Again and Log Out.
   *
   *  Why not reuse `recoverFromDatabaseError`, which would reload once and only then show a
   *  fatal screen. It emits `database_recovery_start` and `database_recovery_fatal`, which would
   *  report a database recovery for something that is not a device fault at all; and it sets the
   *  `sessionStorage` marker that spends the one-reload allowance, so a GENUINE IndexedDB fault
   *  later in the same tab would skip its own first reload. The cost accepted here is that the
   *  user loses one automatic restart attempt. */
  ReportAndShowTheBootFailure = 'ReportAndShowTheBootFailure',
  /** The stored database was repaired in place, but the repair rebuilt nothing this boot needs,
   *  so the app has to restart itself.
   *
   *  Why a successful repair still ends in a reload. The repair reopens the handles of the
   *  databases the app is currently holding. The cold start's own user database is not one of
   *  them: the failed `userDb.initializeAsync` already closed the half-built wrapper and dropped
   *  it, so there is nothing left to reopen. Carrying on from here would show a home screen with
   *  no database behind it and would resolve `whenAppReady` as `true`, which every waiter acts
   *  on. Re-running the cold start instead cannot work either, because several of its steps are
   *  not safe to run twice: `probeHttp2Protocol` appends an iframe per call,
   *  `TelemetryService.initialize` subscribes again with no unsubscribe, `createBootDocWatcher`
   *  overwrites the previous watcher without destroying it, and `FoodDbService.initializeAsync`
   *  builds a new wrapper each call, leaking the previous one into `dbProvider.activeDatabases`.
   *  A reload is the only clean restart available. */
  ReloadToRestartTheColdStart = 'ReloadToRestartTheColdStart',
}

/**
 * Wait for the recovery the cold start just triggered, then say what the cold start should do.
 *
 * @param whenRecoverySettledAsync `DatabaseRecoveryService.whenDatabaseRecoverySettledAsync` — which of
 *   the three outcomes happened. It never hangs, which is what makes awaiting it here safe.
 * @param aNewerColdStartHasBegun Has another cold start started since this one did?
 *
 *   This has to be asked, and asking the wrong way round is worse than not fixing anything
 *   (df-4ebecd016a94). A closed-handle error during a cold start has two causes, and only one
 *   of them is a stuck boot. `design-docs/boot-sequence.md`, "A call on a handle we closed
 *   ourselves is not a broken IndexedDB", says where the error comes from: a second start-up
 *   builds a new database wrapper and shuts the previous one down, so anything still holding
 *   the superseded one is told the database is closed. `supersedePreviousDatabase` in
 *   `services/user-db.service.ts` names the route that produces a second start-up — the
 *   sign-in retry re-emits, so `postLoginAsync` runs again.
 *
 *   So if the OLDER cold start is the one that took the error, a newer one is running and will
 *   take the splash down itself; ending the boot here would replace a perfectly healthy boot
 *   with an error screen. If this is still the newest cold start, nothing else is coming.
 */
export async function decideColdStartRecoveryActionAsync(
  whenRecoverySettledAsync: () => Promise<DatabaseRecoverySettlement>,
  aNewerColdStartHasBegun: boolean,
): Promise<ColdStartRecoveryAction> {
  const settlement = await whenRecoverySettledAsync();
  // the database was repaired under us, but not the part this boot needed — restart the app
  if (settlement === DatabaseRecoverySettlement.RepairedInPlace) {
    return ColdStartRecoveryAction.ReloadToRestartTheColdStart;
  }
  // nothing repaired anything and no reload is coming, so this boot is the only thing that can
  // end the loading screen — unless a newer boot has taken over and will end it instead
  if (settlement === DatabaseRecoverySettlement.NoRepairAndNoReload && !aNewerColdStartHasBegun) {
    return ColdStartRecoveryAction.ReportAndShowTheBootFailure;
  }
  // something else finishes this boot: the reload that is already coming, or the newer cold
  // start that superseded this one
  return ColdStartRecoveryAction.StopWithoutEndingTheBootScreen;
}
